import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  buildGateSelectOptions,
  fetchDefaultGateOptions,
  findGateByCode,
  GateOption,
  getGateCode,
  SELECTED_GATE_CODE_STORAGE_KEY,
  SELECTED_GATE_STORAGE_KEY
} from '../config/gates'

const readStoredGate = (): GateOption | null => {
  const rawGate = window.sessionStorage.getItem(SELECTED_GATE_STORAGE_KEY)

  if (!rawGate) {
    return null
  }

  try {
    const parsedGate = JSON.parse(rawGate)
    const gate = {
      id:
        parsedGate?.id === undefined || parsedGate?.id === null
          ? ''
          : `${parsedGate.id}`,
      integration_code:
        parsedGate?.integration_code === undefined ||
        parsedGate?.integration_code === null
          ? null
          : `${parsedGate.integration_code}`,
      alias:
        parsedGate?.alias === undefined || parsedGate?.alias === null
          ? null
          : `${parsedGate.alias}`,
      label:
        parsedGate?.label === undefined || parsedGate?.label === null
          ? ''
          : `${parsedGate.label}`
    }

    return getGateCode(gate) ? gate : null
  } catch {
    return null
  }
}

const readStoredGateCode = (): string =>
  getGateCode(readStoredGate()) ||
  `${window.sessionStorage.getItem(SELECTED_GATE_CODE_STORAGE_KEY) || ''}`.trim()

const persistSelectedGate = (gate: GateOption | null): void => {
  if (!gate || !getGateCode(gate)) {
    window.sessionStorage.removeItem(SELECTED_GATE_STORAGE_KEY)
    window.sessionStorage.removeItem(SELECTED_GATE_CODE_STORAGE_KEY)
    return
  }

  window.sessionStorage.setItem(SELECTED_GATE_STORAGE_KEY, JSON.stringify(gate))
  window.sessionStorage.setItem(SELECTED_GATE_CODE_STORAGE_KEY, getGateCode(gate))
}

export const useGateOptions = () => {
  const [gates, setGates] = useState<GateOption[]>([])
  const [selectedGate, setSelectedGate] = useState<GateOption | null>(
    readStoredGate
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const gateOptions = useMemo(() => buildGateSelectOptions(gates), [gates])

  const selectGate = useCallback((gate: GateOption | null) => {
    setSelectedGate(gate)
    persistSelectedGate(gate)
  }, [])

  const selectGateByCode = useCallback(
    (code: string) => {
      selectGate(findGateByCode(gates, code))
    },
    [gates, selectGate]
  )

  const reload = useCallback(async () => {
    setIsLoading(true)
    setError('')

    try {
      const nextGates = await fetchDefaultGateOptions()
      const storedGateCode = readStoredGateCode()
      const nextSelectedGate = findGateByCode(nextGates, storedGateCode)

      setGates(nextGates)
      selectGate(nextSelectedGate)
    } catch (loadError) {
      console.error('Erro ao carregar portões:', loadError)
      setError('Não foi possível carregar os portões.')
    } finally {
      setIsLoading(false)
    }
  }, [selectGate])

  useEffect(() => {
    let isActive = true

    const loadGates = async () => {
      setIsLoading(true)
      setError('')

      try {
        const nextGates = await fetchDefaultGateOptions()

        if (!isActive) {
          return
        }

        const storedGateCode = readStoredGateCode()
        const nextSelectedGate = findGateByCode(nextGates, storedGateCode)

        setGates(nextGates)
        selectGate(nextSelectedGate)
      } catch (loadError) {
        console.error('Erro ao carregar portões:', loadError)

        if (isActive) {
          setError('Não foi possível carregar os portões.')
        }
      } finally {
        if (isActive) {
          setIsLoading(false)
        }
      }
    }

    loadGates()

    return () => {
      isActive = false
    }
  }, [selectGate])

  return {
    gates,
    gateOptions,
    selectedGate,
    selectGateByCode,
    isLoading,
    error,
    reload
  }
}
